import * as React from "react";
import {
  Stack,
  Typography,
  Button,
  Checkbox,
  TextField,
  Box,
} from "@mui/material";
import {
  ServerPaginatedTable,
  CustomeDialog,
  useDropdownApis,
} from "../../../shared";
import InputControl from "../../../shared/components/InputControl";

import { useTandaSyncHistory } from "../hooks/useTandaSyncHistory";
import moment from "moment";

export const SyncHistory = ({ storeId }) => {
  const {
    syncHistory,
    isLoading,
    handleSearch,
    handleSort,
    gotoPage,
    setPageSize,
    handleMoreModal,
    onChangeDate,
    handleStatusChange,
    handleFilterClear,
    handleApplyFilter,
  } = useTandaSyncHistory(storeId);

  // Character limit before showing the "More" button
  const CHAR_LIMIT = 70;

  // Status options for the dropdown filter
  const statusOptions = [
    { value: "success", label: "Success" },
    { value: "failure", label: "Failure" },
    { value: "pending", label: "Pending" },
  ];

  const columns = React.useMemo(
    () => [
      {
        Header: "Sync Date",
        accessor: "sync_date",
        Cell: ({ value }) => value ?? "--",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              color:
                value === "success"
                  ? "#0F875B"
                  : value === "failure"
                  ? "#C42B1C"
                  : value === "pending"
                  ? "#2a3ac9"
                  : "#636363",
              fontWeight: 600,
              textTransform: "capitalize",
            }}
          >
            {value}
          </Typography>
        ),
      },
      {
        Header: "Remarks",
        accessor: "remark",
        Cell: ({ value }) => {
          if (!value) return "--";

          const isLongText = value.length > CHAR_LIMIT;

          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                sx={{
                  maxWidth: "360px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {value}
              </Typography>
              {isLongText && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleMoreModal(true, value)}
                  sx={{
                    minWidth: "40px",
                    height: "24px",
                    fontSize: "12px",
                    p: "4px 8px",
                  }}
                >
                  More
                </Button>
              )}
            </Stack>
          );
        },
      },
    ],
    [handleMoreModal]
  );

  return (
    <>
      <ServerPaginatedTable
        columns={columns}
        data={syncHistory.data}
        tableTitle="Sync History"
        isLoading={isLoading}
        filterApplied={syncHistory.filterApplied.all}
        defaultSearch={false}
        onFilterClear={(e) => handleFilterClear("single", e)}
        onAllFilterClear={(e) => handleFilterClear("all")}
        gotoPage={gotoPage}
        setPageSize={setPageSize}
        pagination={syncHistory.pagination}
        sortByGlobal={syncHistory.sortBy}
        handleSort={handleSort}
        filterContent={
          <>
            <Stack
              direction="row"
              gap="16px"
              alignItems="center"
              flexWrap="wrap"
              width="100%"
            >
              <InputControl
                type="table-search"
                value={syncHistory.filterApplied.search}
                onChange={(e) => handleSearch(e.target.value)}
                onClear={() => handleSearch("")}
                sx={{ width: "calc(100% - 433px)", minWidth: "200px" }}
                placeholder="Search"
              />
              <InputControl
                type="datePicker"
                name="sync_date"
                label="Sync Date"
                inputFormat="dd-MM-yyyy"
                disableFuture
                value={syncHistory.syncDate || null}
                onChange={(date) => {
                  onChangeDate({
                    value: date,
                    label: date ? moment(date).format("DD-MM-YYYY") : "",
                    type: "Sync Date",
                  });
                }}
                sx={{ width: 200 }}
              />

              <InputControl
                type="select"
                name="status"
                label="Status"
                value={syncHistory.filterApplied.status?.value || ""}
                optionValue={statusOptions}
                onChange={(e) =>
                  handleStatusChange({
                    value: e.target.value,
                    label:
                      statusOptions.find(
                        (option) => option.value === e.target.value
                      )?.label || "",
                    type: "Status",
                  })
                }
                width="200px"
              />
            </Stack>

            <Button
              type="button"
              size="medium"
              variant="text"
              color="info"
              sx={{
                minWidth: "40px",
                p: "8px",
              }}
              onClick={handleApplyFilter}
              disabled={syncHistory.disableApplyButton}
            >
              Apply
            </Button>
          </>
        }
      />

      <CustomeDialog
        open={syncHistory.moreModal}
        handleClose={() => handleMoreModal(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { width: "500px", p: "16px", maxHeight: "500px" },
        }}
        title="Remarks"
        content={
          <Stack sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
              {syncHistory.moreText}
            </Typography>
          </Stack>
        }
      />
    </>
  );
};

export default SyncHistory;
